import typeCheck from 'common/util/typeCheck';

import {
  HOUSEHOLD_APPLICATION_STATUS_LIST,
  HOUSEHOLD_APPLICATION_STATUS_REQUESTED,
  HOUSEHOLD_APPLICATION_STATUS_STARTED,
  HOUSEHOLD_APPLICATION_STATUS_COMPLETED,
  HOUSEHOLD_APPLICATION_CATEGORY_HOUSEHOLD_APPLICATION,
} from 'common/householdConstants';

import { findHouseholdApplication } from 'common/householdApplication/applicationHelpers';

import { APPLICATION_FORM_TYPE_RENTER_PROFILE } from 'common/householdApplication/applicationFormFields';

import {
  SR_API_ACTION_HOUSEHOLD_START_APPLICATION,
  SR_API_ACTION_HOUSEHOLD_GET_BY_USER_ID,
  SR_API_ACTION_HOUSEHOLD_LISTING_GET_BY_HOUSEHOLD_AND_LISTING_ID,
} from 'api/actionTypes';

import getHouseholdApplicationsAction from 'client/actions/Household/getApplications';
import loadRenterProfileAction from 'client/actions/Household/loadRenterProfile';
import redirectToApplicationFormAction from 'client/actions/Household/redirectToApplicationForm';
import findBuyersAgentAction from 'client/actions/Household/findBuyersAgent';

export default async function startApplication(
  { state, history, apiClient },
  { listingId, shouldRestart } = {},
) {
  typeCheck('listingId::Maybe PositiveNumber', listingId);
  typeCheck('history::NonEmptyObject', history);

  const currentUserId = state.get(['currentUser', 'id']);
  const currentHousehold = await apiClient.post(SR_API_ACTION_HOUSEHOLD_GET_BY_USER_ID, {
    userId: currentUserId,
  });

  if (listingId) {
    await apiClient.postWithState({
      action: SR_API_ACTION_HOUSEHOLD_START_APPLICATION,
      state: state,
      payload: {
        listingId,
      },
    });
  }

  await Promise.all([
    getHouseholdApplicationsAction(
      { state, apiClient },
      {
        userId: currentUserId,
      },
    ),
    loadRenterProfileAction(
      { state, apiClient },
      {
        userId: currentUserId,
      },
    ),
  ]);

  const householdApplication = findHouseholdApplication({
    householdApplications: state.get(['householdApplications']),
    category: HOUSEHOLD_APPLICATION_CATEGORY_HOUSEHOLD_APPLICATION,
    listingId: listingId,
  });

  if (householdApplication) {
    const householdListing = await apiClient.post(
      SR_API_ACTION_HOUSEHOLD_LISTING_GET_BY_HOUSEHOLD_AND_LISTING_ID,
      {
        householdId: currentHousehold.id,
        listingId: listingId,
      },
    );

    // No buyers agent information in the householdListing
    if (
      householdListing &&
      householdListing.inPersonTour &&
      typeof householdListing.hasBuyersAgent !== 'boolean'
    ) {
      const result = await findBuyersAgentAction({ state, history, apiClient }, listingId);

      if (result.buyerSideAgent || result.supraAgents.length) {
        history.push(`/application/${currentUserId}/${listingId}/agent`);
        return;
      }
    }
  }

  const renterProfileStatus = state.get([
    'household',
    'renterProfilesByUserId',
    currentUserId,
    'status',
  ]);
  const hasCompletedRenterProfile = renterProfileStatus === HOUSEHOLD_APPLICATION_STATUS_COMPLETED;

  const hasCompletedHouseholdApplications = state
    .get(['householdApplications'])
    .some(({ status }) => {
      const indexOfCurrAppStatus = HOUSEHOLD_APPLICATION_STATUS_LIST.indexOf(status);
      const indexOfStartedAppStatus = HOUSEHOLD_APPLICATION_STATUS_LIST.indexOf(
        HOUSEHOLD_APPLICATION_STATUS_STARTED,
      ); // eslint-disable-line max-len
      return indexOfCurrAppStatus > indexOfStartedAppStatus;
    });

  if (
    !hasCompletedHouseholdApplications &&
    !hasCompletedRenterProfile &&
    listingId &&
    (!householdApplication ||
      householdApplication.status === HOUSEHOLD_APPLICATION_STATUS_REQUESTED)
  ) {
    history.push(`/application/${currentUserId}/${listingId}/start`);
  } else {
    await redirectToApplicationFormAction(
      { state, history, apiClient },
      {
        listingId: listingId,
        formType: APPLICATION_FORM_TYPE_RENTER_PROFILE,
        shouldRestart,
      },
    );
  }
}
